import React, { useState } from 'react';
import { injectIntl, intlShape } from 'react-intl';
import {
    Tab, Tabs, TabScrollButton, Typography, Grid, Box, useTheme, useMediaQuery
} from '@mui/material';
import PropTypes from 'prop-types';
import { List as IList } from 'immutable';
import { v4 as uuid } from 'uuid';

import { connect } from 'react-redux';
import windowSize from '../../utils/windowSize';
import FaceplateList from '../Carousel/FaceplateList';
import FaceplateCarousel from '../Carousel/FaceplateCarousel';
import homePageV2Messages from '../assets/messages/homePageMessages';
import SkeletonLoader from '../../loader/SkeletonLoader';
import CarousalCategoryPicker from '../../giftCardDetailsPage_v2/CarousalCategoryPicker';
import { CARD_TYPES } from '../../constants/constants';
import { HOME_STEP } from '../../routing/flowConstants';
import UYOButton from '../../common/UYOButton';
import {
    saveSelectedCardOnBuyButtonClick,
    selectedCardType, setClickedOnUYO
} from '../../landingPage/landingPageModule';
import { trackButtonClick } from '../../segment/segmentModule';
import { logWarning } from '../../utils/errorUtils';


function AccessibleTabScrollButton(props) {
    const { direction } = props;
    return (
        <TabScrollButton
            {...props}
            component='button'
            type='button'
            aria-label={direction === 'left' ? 'Go to previous design category' : 'Go to next design category'}
        />
    );
}

AccessibleTabScrollButton.propTypes = {
    direction: PropTypes.oneOf(['left', 'right']).isRequired
};

export function CategoryTabsCarouselContainer({
    handleSubmit,
    categories,
    giftCardDeliveryType,
    categoryCount,
    setFaceplatesCount,
    faceplatesCount,
    intl,
    isLoading,
    callbackOnCategorySelectOrClose,
    tempCurrency,
    shouldSetCurrency,
    cardType,
    renderCatalogAndDenom,
    isPhotoSupported,
    isPhysicalCard,
    uyoFaceplate,
    defaultCategory,
    defaultFaceplate,
    saveSelectedCardOnBuyButtonClickAction,
    saveSelectedCardType,
    setClickedOnUYOAction,
    trackAddAnotherButton,
    isNewLandingPage,
    hidePicker,
    checkoutSubHeaderText
}) {
    const faceplateCategory = categories && categories.toJS()[0];
    const [selectedCategory, setSelectedCategory] = useState(faceplateCategory);
    const [selectedCategoryTabIndex, setSelectedCategoryTabIndex] = useState(0);

    const [carouselKey, setCarouselKey] = useState(0);
    const [carouselLoaded, setCarouselLoaded] = useState(false);


    const hasCategories = (categories.size >= 1);

    
    const handleTabChange = (event, newValue) => {
        const changedCategory = categories.toJS()[newValue];
        setSelectedCategory(changedCategory);
        setSelectedCategoryTabIndex(newValue);
        setCarouselLoaded(false);
        trackAddAnotherButton(changedCategory, HOME_STEP, 'Category Pill Click');
    };

    const refreshCarousel = () => setCarouselKey(prevKey => prevKey + 1);

    const handleScrollButtonClick = (e) => {
        try {
            const scrollButton = e.target.closest('.MuiTabScrollButton-root');
            if (!scrollButton) return;
            const container = e.currentTarget;
            if (!container) return;
            const allButtons = container.querySelectorAll('.MuiTabScrollButton-root');
            if (!allButtons || allButtons.length === 0) return;
            const dir = scrollButton === allButtons[0] ? 'Left' : 'Right';
            const cardTypeLabel = isPhysicalCard ? CARD_TYPES.Physical : CARD_TYPES.Digital;
            trackAddAnotherButton(
                `${cardTypeLabel} Category Tab ${dir} Arrow Click`,
                HOME_STEP,
                'Homepage Category Tab Arrow Click'
            );
        } catch (err) {
            logWarning('Category tab arrow tracking failed', { error: err.message });
        }
    };

    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            {(hasCategories) && (
                <Box>
                    {}
                    <Grid
                        container
                        className={`choose-a-design-container ${!(faceplatesCount > 1 || categoryCount > 1)
                            ? 'single-faceplate-choose-a-design-container' : ''}`}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <Box className='faceplate-header-section'>
                            <Grid item className='faceplate-header-wrapper'>
                                {(faceplatesCount > 1 || categoryCount > 1) && (
                                    <SkeletonLoader
                                        isLoading={isLoading}
                                        skeletonProps={{
                                            animation: 'wave',
                                            variant: 'text',
                                            className: 'skeletonclass',
                                            height: 85
                                        }}
                                    >
                                        <Typography id='design-heading' variant="h3" className='faceplateHeaderText' lang={intl.locale}>
                                            {intl.formatMessage(homePageV2Messages.CarouselheadingTitle)}
                                        </Typography>
                                    </SkeletonLoader>
                                )}
                                {
                                    isPhotoSupported && isNewLandingPage && (faceplatesCount > 1 || categoryCount > 1) && (
                                        <UYOButton
                                            showLabel={!isMobileView}
                                            onClick={() => {
                                                saveSelectedCardType(isPhysicalCard ? CARD_TYPES.Physical : CARD_TYPES.Digital);
                                                if (!uyoFaceplate) {
                                                    saveSelectedCardOnBuyButtonClickAction(defaultCategory,
                                                        defaultFaceplate,
                                                        'UYO',
                                                        isPhysicalCard);
                                                }
                                                setClickedOnUYOAction(true); 
                                                handleSubmit(isPhysicalCard, true);
                                                trackAddAnotherButton('Upload Your Own Photo', HOME_STEP, 'Homepage');
                                            }}
                                        />
                                    )

                                }
                            </Grid>
                        </Box>

                        <Box className='category-tabs-section'>
                            <Grid item xs className='category-warpper'>
                                {}
                                {categoryCount > 1 && (
                                    <Box sx={{ display: { xs: 'none', md: 'block' } }} onClick={handleScrollButtonClick}>
                                        <SkeletonLoader
                                            isLoading={isLoading}
                                            skeletonProps={{
                                                animation: 'wave',
                                                variant: 'text',
                                                className: 'skeletonclass',
                                                height: 85
                                            }}
                                        >
                                            <Tabs
                                                value={selectedCategoryTabIndex}
                                                onChange={handleTabChange}
                                                aria-label='Design categories'
                                                className='category-tabs-wrapper'
                                                variant="scrollable"
                                                scrollButtons="auto"
                                                ScrollButtonComponent={AccessibleTabScrollButton}
                                            >
                                                {categories.map((category, index) => {
                                                    const tabId = `tab-${index}`;
                                                    const panelId = `tabpanel-${index}`;
                                                    const isSelected = selectedCategoryTabIndex === index;
                                                    return (
                                                        <Tab
                                                            id={tabId}
                                                            aria-controls={panelId}
                                                            aria-selected={isSelected}
                                                            className='category-tabs'
                                                            label={category}
                                                            value={index}
                                                            key={uuid()}
                                                            onClick={refreshCarousel}
                                                        />
                                                    );
                                                })}
                                            </Tabs>
                                        </SkeletonLoader>
                                    </Box>
                                )}
                                {}
                                {categoryCount > 1 && (
                                    <>
                                        <Box className='CarousalCategoryPicker-mobile' sx={{ display: { xs: 'block', md: 'none' } }}>
                                            <SkeletonLoader
                                                isLoading={isLoading}
                                                skeletonProps={{
                                                    animation: 'wave',
                                                    variant: 'text',
                                                    className: 'skeletonclass',
                                                    height: 85,
                                                    width: 100
                                                }}
                                            >
                                                <CarousalCategoryPicker
                                                    selectedFaceplateCategory={selectedCategory}
                                                    allCategoryList={categories}
                                                    onCategorySelect={setSelectedCategory}
                                                    refreshCarousel={refreshCarousel}
                                                    callbackOnCategorySelectOrClose={callbackOnCategorySelectOrClose}
                                                    stepName={HOME_STEP}
                                                    cardType={cardType}
                                                />
                                            </SkeletonLoader>
                                        </Box>
                                    </>
                                )}
                            </Grid>
                        </Box>

                    </Grid>
                    {categories.map((category, index) => {
                        const tabId = `tab-${index}`;
                        const panelId = `tabpanel-${index}`;
                        const isSelected = selectedCategoryTabIndex === index;
                        return (
                            <>
                                {isSelected && (
                                    <Grid
                                        container
                                        key={panelId}
                                        role="tabpanel"
                                        id={panelId}
                                        aria-labelledby={tabId}
                                        sx={{ display: isSelected ? 'block' : 'none' }}
                                    >
                                        <Grid item xs={12} className='carousal-body-wrapper'>
                                            { isNewLandingPage
                                                ? (
                                                    <FaceplateList
                                                        category={selectedCategory}
                                                        handleSubmit={handleSubmit}
                                                        categoryCount={categoryCount}
                                                        carouselKey={carouselKey}
                                                        giftCardDeliveryType={giftCardDeliveryType}
                                                        setFaceplatesCount={setFaceplatesCount}
                                                        setCarouselLoaded={setCarouselLoaded}
                                                        carouselLoaded={carouselLoaded}
                                                        intl={intl}
                                                        tempCurrency={tempCurrency}
                                                        shouldSetCurrency={shouldSetCurrency}
                                                        cardType={cardType}
                                                        renderCatalogAndDenom={renderCatalogAndDenom}
                                                        isPhotoSupported={isPhotoSupported}
                                                        isPhysicalCard={isPhysicalCard}
                                                        uyoFaceplate={uyoFaceplate}
                                                        isMobileView={isMobileView}
                                                        hidePicker={hidePicker}
                                                        checkoutSubHeaderText={checkoutSubHeaderText}
                                                    />
                                                )
                                                : (
                                                    <FaceplateCarousel
                                                        category={selectedCategory}
                                                        handleSubmit={handleSubmit}
                                                        categoryCount={categoryCount}
                                                        carouselKey={carouselKey}
                                                        giftCardDeliveryType={giftCardDeliveryType}
                                                        setFaceplatesCount={setFaceplatesCount}
                                                        setCarouselLoaded={setCarouselLoaded}
                                                        carouselLoaded={carouselLoaded}
                                                        intl={intl}
                                                        tempCurrency={tempCurrency}
                                                        shouldSetCurrency={shouldSetCurrency}
                                                    />
                                                )
                                            }
                                        </Grid>
                                    </Grid>
                                )}
                            </>
                        );
                    })}
                </Box>
            )}
        </>
    );
}

CategoryTabsCarouselContainer.defaultProps = {
    callbackOnCategorySelectOrClose: () => { },
    cardType: '',
    renderCatalogAndDenom: () => null,
    isNewLandingPage: false,
    hidePicker: false,
    checkoutSubHeaderText: ''
};
CategoryTabsCarouselContainer.propTypes = {
    categories: PropTypes.instanceOf(IList).isRequired,
    handleSubmit: PropTypes.func.isRequired,
    giftCardDeliveryType: PropTypes.string.isRequired,
    categoryCount: PropTypes.number.isRequired,
    faceplatesCount: PropTypes.number.isRequired,
    setFaceplatesCount: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    isLoading: PropTypes.bool.isRequired,
    callbackOnCategorySelectOrClose: PropTypes.func,
    tempCurrency: PropTypes.string.isRequired,
    shouldSetCurrency: PropTypes.bool.isRequired,
    cardType: PropTypes.string,
    renderCatalogAndDenom: PropTypes.func,
    saveSelectedCardOnBuyButtonClickAction: PropTypes.func.isRequired,
    saveSelectedCardType: PropTypes.func.isRequired,
    setClickedOnUYOAction: PropTypes.func.isRequired,
    trackAddAnotherButton: PropTypes.func.isRequired,
    isPhotoSupported: PropTypes.bool.isRequired,
    isPhysicalCard: PropTypes.bool.isRequired,
    uyoFaceplate: PropTypes.string.isRequired,
    defaultCategory: PropTypes.string.isRequired,
    defaultFaceplate: PropTypes.string.isRequired,
    isNewLandingPage: PropTypes.bool,
    hidePicker: PropTypes.bool,
    checkoutSubHeaderText: PropTypes.string
};

const mapDispatchToProps = {
    saveSelectedCardOnBuyButtonClickAction: saveSelectedCardOnBuyButtonClick,
    saveSelectedCardType: selectedCardType,
    setClickedOnUYOAction: setClickedOnUYO,
    trackAddAnotherButton: trackButtonClick
};

export default connect(null, mapDispatchToProps)(injectIntl(windowSize(CategoryTabsCarouselContainer)));
